import { useState } from 'react';

export interface WorkshopVideo {
  id: string;
  title: string;
  vimeo_id: string;
  description: string;
}

function extractVimeoId(input: string): string {
  const trimmed = input.trim();
  if (/^\d+$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : trimmed;
}

interface WorkshopManagerProps {
  videos: WorkshopVideo[];
  onChange: (videos: WorkshopVideo[]) => void;
}

export default function WorkshopManager({ videos, onChange }: WorkshopManagerProps) {
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  const addVideo = () => {
    const newVideo: WorkshopVideo = {
      id: crypto.randomUUID(),
      title: '',
      vimeo_id: '',
      description: '',
    };
    onChange([...videos, newVideo]);
  };

  const updateVideo = (id: string, field: keyof WorkshopVideo, value: string) => {
    onChange(
      videos.map((v) =>
        v.id === id
          ? { ...v, [field]: field === 'vimeo_id' ? extractVimeoId(value) : value }
          : v,
      ),
    );
  };

  const removeVideo = (id: string) => {
    onChange(videos.filter((v) => v.id !== id));
    if (previewingId === id) setPreviewingId(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Taller Práctico</p>
        <p className="text-[#F8F6F3]/40 text-sm">
          Añade uno o varios videos de Vimeo para los ejercicios del módulo
        </p>
      </div>

      {/* Video list */}
      {videos.length > 0 && (
        <div className="flex flex-col gap-4">
          {videos.map((video, index) => (
            <div
              key={video.id}
              className="rounded-2xl p-5 flex flex-col gap-4"
              style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.08)' }}
            >
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: 'rgba(139,157,131,0.12)', border: '1px solid rgba(139,157,131,0.20)' }}
                  >
                    <i className="ri-play-circle-line text-[#8B9D83] text-xs" />
                  </div>
                  <span className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">
                    Ejercicio {index + 1}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewingId(previewingId === video.id ? null : video.id)}
                    disabled={!video.vimeo_id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all duration-200 hover:opacity-80 disabled:opacity-30 whitespace-nowrap"
                    style={{ background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.25)' }}
                  >
                    <i className={previewingId === video.id ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    {previewingId === video.id ? 'Ocultar' : 'Preview'}
                  </button>
                  <button
                    onClick={() => removeVideo(video.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200 hover:opacity-70"
                    style={{ background: 'rgba(201,123,99,0.10)', border: '1px solid rgba(201,123,99,0.15)' }}
                  >
                    <i className="ri-delete-bin-line text-[#C97B63] text-sm" />
                  </button>
                </div>
              </div>

              {/* Title */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">Título del ejercicio</label>
                <input
                  type="text"
                  value={video.title}
                  onChange={(e) => updateVideo(video.id, 'title', e.target.value)}
                  placeholder="Ej: Ejercicio de integración — Día 1"
                  className="w-full px-4 py-3 rounded-xl text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none"
                  style={{ background: 'rgba(248,246,243,0.06)', border: '1px solid rgba(248,246,243,0.10)' }}
                />
              </div>

              {/* Vimeo URL */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">URL o ID de Vimeo</label>
                <input
                  type="text"
                  value={video.vimeo_id}
                  onChange={(e) => updateVideo(video.id, 'vimeo_id', e.target.value)}
                  placeholder="https://vimeo.com/123456789 o 123456789"
                  className="w-full px-4 py-3 rounded-xl text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none"
                  style={{ background: 'rgba(248,246,243,0.06)', border: '1px solid rgba(248,246,243,0.10)' }}
                />
                {video.vimeo_id && !/^\d+$/.test(video.vimeo_id) === false && (
                  <p className="text-[#8B9D83]/60 text-[10px]">ID detectado: {video.vimeo_id}</p>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">Descripción (opcional)</label>
                <textarea
                  value={video.description}
                  onChange={(e) => updateVideo(video.id, 'description', e.target.value)}
                  placeholder="Breve descripción del ejercicio..."
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none resize-none"
                  style={{ background: 'rgba(248,246,243,0.06)', border: '1px solid rgba(248,246,243,0.10)' }}
                />
              </div>

              {/* Inline preview */}
              {previewingId === video.id && video.vimeo_id && (
                <div className="rounded-xl overflow-hidden" style={{ aspectRatio: '16/9', background: '#0D0F12' }}>
                  <iframe
                    src={`https://player.vimeo.com/video/${video.vimeo_id}?badge=0&autopause=0&color=8B9D83&title=0&byline=0&portrait=0`}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={video.title || 'Preview'}
                    style={{ border: 'none' }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add button */}
      <button
        onClick={addVideo}
        className="flex items-center justify-center gap-2 py-5 rounded-2xl text-sm font-medium cursor-pointer transition-all duration-200 hover:opacity-80 whitespace-nowrap"
        style={{ background: 'rgba(139,157,131,0.06)', color: '#8B9D83', border: '1px dashed rgba(139,157,131,0.30)' }}
      >
        <i className="ri-add-circle-line text-base" />
        Añadir video de ejercicio
      </button>

      {/* Empty hint */}
      {videos.length === 0 && (
        <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
          <div className="w-14 h-14 flex items-center justify-center">
            <i className="ri-video-add-line text-[#F8F6F3]/12 text-4xl" />
          </div>
          <p className="text-[#F8F6F3]/25 text-sm">Sin videos de taller aún</p>
          <p className="text-[#F8F6F3]/15 text-xs max-w-xs leading-relaxed">
            Pulsa el botón de arriba para añadir el primer video de ejercicio
          </p>
        </div>
      )}
    </div>
  );
}
